"use client";

import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import { Globe, Image as ImageIcon, UploadCloud } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { compressImage } from "../lib/image-compression";

export type ImageUploadTabsProps = {
	onImageSelected: (file: File | null, url: string) => void;
	className?: string;
	maxSize?: number; // Maximum file size in bytes
	acceptedFileTypes?: Record<string, string[]>; // Accepted file types
};

export function ImageUploadTabs({
	onImageSelected,
	className,
	maxSize = 10 * 1024 * 1024, // Default 10MB
	acceptedFileTypes = {
		"image/png": [".png"],
		"image/jpeg": [".jpg", ".jpeg"],
		"image/webp": [".webp"],
	},
}: ImageUploadTabsProps) {
	const t = useTranslations();
	const [activeTab, setActiveTab] = useState<"upload" | "url">("upload");
	const [imageUrl, setImageUrl] = useState<string>("");

	// Handle file drop and compression
	const { getRootProps, getInputProps } = useDropzone({
		onDrop: async (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				const file = acceptedFiles[0];

				try {
					// Compress image silently
					const compressedFile = await compressImage(file);
					onImageSelected(compressedFile, "");
				} catch (error) {
					console.error("Image compression error:", error);
					// If compression fails, use original file
					onImageSelected(file, "");
				}
			}
		},
		accept: acceptedFileTypes,
		maxSize,
		multiple: false,
	});

	// Handle URL input
	const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setImageUrl(e.target.value);
	};

	// Handle URL submit
	const handleUrlSubmit = () => {
		if (imageUrl.trim()) {
			onImageSelected(null, imageUrl);
		}
	};

	// Handle Enter key submit for URL
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			handleUrlSubmit();
		}
	};

	return (
		<div className={cn("w-full", className)}>
			<Tabs
				defaultValue="upload"
				value={activeTab}
				onValueChange={(value) =>
					setActiveTab(value as "upload" | "url")
				}
				className="w-full"
			>
				<TabsList className="w-full">
					<TabsTrigger value="upload" className="flex-1">
						<UploadCloud className="mr-2 h-5 w-5" />
						{t("imageToPrompt.components.uploadTabs.uploadFile")}
					</TabsTrigger>
					<TabsTrigger value="url" className="flex-1">
						<Globe className="mr-2 h-5 w-5" />
						{t("imageToPrompt.components.uploadTabs.imageUrl")}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="upload" className="mt-2">
					<div
						{...getRootProps()}
						className="flex items-center justify-center h-72 bg-transparent cursor-pointer transition-colors border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/20 rounded-lg"
					>
						<input {...getInputProps()} />
						<div className="flex flex-col items-center justify-center text-center p-4">
							<ImageIcon
								className="text-indigo-500 mb-2 h-8 w-8"
								aria-label="Image placeholder"
							/>
							<p className="text-gray-400 font-medium">
								{t(
									"imageToPrompt.components.uploadTabs.uploadInstruction",
								)}
							</p>
							<p className="text-xs text-muted-foreground mt-1">
								{t(
									"imageToPrompt.components.uploadTabs.fileTypes",
									{ size: maxSize / (1024 * 1024) },
								)}
							</p>
						</div>
					</div>
				</TabsContent>

				<TabsContent value="url" className="mt-2">
					<div className="flex flex-col justify-center h-72 p-4 border-2 border-dashed border-gray-200 rounded-lg">
						<div className="flex items-center gap-2">
							<Input
								type="url"
								placeholder="https://example.com/image.jpg"
								value={imageUrl}
								onChange={handleUrlChange}
								onKeyDown={handleKeyDown}
								className="flex-1"
							/>
							<Button
								onClick={handleUrlSubmit}
								type="button"
								variant={
									imageUrl.trim() ? "primary" : "outline"
								}
							>
								{t("imageToPrompt.components.uploadTabs.load")}
							</Button>
						</div>
						<p className="text-xs text-muted-foreground mt-2">
							{t(
								"imageToPrompt.components.uploadTabs.enterUrlHint",
							)}
						</p>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	);
}
